import { ArrowUpRight, Activity, Dumbbell, Leaf, HeartHandshake, Briefcase, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { BOOKING_URL, SERVICES } from "@/lib/constants";
import { absoluteUrl } from "@/lib/seo-config";

const iconMap = { Activity, Dumbbell, Leaf, HeartHandshake, Briefcase };

const servicesSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: SERVICES.map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Service",
      name: s.category,
      description: s.description,
      provider: { "@type": "MedicalBusiness", name: "Ashlan Clinic" },
      areaServed: { "@type": "Country", name: "Bermuda" },
      url: absoluteUrl("/services"),
    },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
    { "@type": "ListItem", position: 2, name: "Services", item: absoluteUrl("/services") },
  ],
};

export default function Services() {
  return (
    <main data-testid="services-page" className="bg-white">
      <SEO
        title="Services — Clinical Massage Therapy in Bermuda"
        description="Pain & Rehab, Sports Performance, Wellness & Recovery, Women's Care and Corporate Wellness — clinical treatment organised around outcomes."
        path="/services"
        schemas={[servicesSchema, breadcrumbSchema]}
      />
      {/* Header */}
      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <span className="ash-eyebrow">Services</span>
          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-[#080808] max-w-4xl">
            Clinical care, organised around outcomes.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#3C4048] max-w-2xl leading-relaxed">
            Every service is delivered by experienced therapists, follows a
            structured clinical process, tailored individually, and is
            measured against your starting baseline.
          </p>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid gap-6 md:gap-8">
          {SERVICES.map((s, i) => {
            const Icon = iconMap[s.icon] || Activity;
            return (
              <article
                key={s.category}
                data-testid={`service-category-${i}`}
                className="grid md:grid-cols-12 gap-8 border border-[#e5e7eb] p-8 md:p-12 hover:border-[#3A7A8C] transition-colors"
              >
                <div className="md:col-span-1">
                  <Icon
                    size={28}
                    strokeWidth={1.4}
                    className="text-[#3A7A8C]"
                  />
                </div>
                <div className="md:col-span-5">
                  <span className="ash-eyebrow">Category {String(i + 1).padStart(2, "0")}</span>
                  <h2 className="mt-3 text-3xl md:text-4xl font-bold text-[#080808] tracking-tight leading-tight">
                    {s.category}
                  </h2>
                  <p className="mt-4 text-[#3C4048] leading-relaxed">
                    {s.description}
                  </p>
                  <a
                    href={BOOKING_URL}
                    data-testid={`service-book-${i}`}
                    className="ash-link-underline mt-6 inline-flex"
                  >
                    Book {s.category} <ArrowUpRight size={14} />
                  </a>
                </div>
                <div className="md:col-span-6">
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {s.items.map((item, idx) => (
                      <li
                        key={idx}
                        data-testid={`service-item-${i}-${idx}`}
                        className="flex items-start gap-3 text-[#080808] font-medium"
                      >
                        <span className="ash-rule mt-3 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="bg-[#F5F5F5] border-y border-[#e5e7eb]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[#080808] leading-tight">
              Not sure which service fits?
            </h2>
            <p className="mt-4 text-lg text-[#3C4048] max-w-2xl">
              Tell us what’s bringing you in and we’ll route you to the right
              therapist.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right flex md:justify-end">
            <a
              href={BOOKING_URL}
              data-testid="services-cta-book"
              className="ash-btn-primary"
            >
              Book a Session <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
